// src/components/MultiMediaUpload.js
import React, { useState, useRef, useEffect } from 'react';
import './MultiMediaUpload.css';

function MultiMediaUpload({ onMediaSelected, initialMedia = [], maxFiles = 10 }) {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB for videos, 2MB for images
  const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
  const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo'];
  const ACCEPTED_TYPES = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES];

  // Initialize with existing media when component mounts or initialMedia changes
  useEffect(() => {
    if (initialMedia && initialMedia.length > 0) {
      setMediaPreviews(initialMedia);
      // Don't set mediaFiles for existing media since they're already uploaded
      setMediaFiles([]);
    }
  }, [initialMedia]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newErrors = [];
    const validFiles = [];
    const validPreviews = [];

    // Check if adding these files would exceed the limit
    if (mediaFiles.length + files.length > maxFiles) {
      newErrors.push(`Maximum ${maxFiles} files allowed`);
      setErrors(newErrors);
      return;
    }

    files.forEach((file, index) => {
      // Validate file type
      if (!ACCEPTED_TYPES.includes(file.type)) {
        newErrors.push(`File ${file.name}: Invalid file type. Only images (JPG, PNG, GIF) and videos (MP4, MPEG, MOV, AVI) are allowed.`);
        return;
      }

      // Validate file size
      const isVideo = ACCEPTED_VIDEO_TYPES.includes(file.type);
      const maxSize = isVideo ? MAX_FILE_SIZE : 2 * 1024 * 1024;
      
      if (file.size > maxSize) {
        const sizeMB = isVideo ? 10 : 2;
        newErrors.push(`File ${file.name}: Size must be less than ${sizeMB}MB`);
        return;
      }

      validFiles.push(file);

      // Create preview
      if (ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        const reader = new FileReader();
        reader.onload = () => {
          validPreviews.push({
            url: reader.result,
            type: 'image',
            name: file.name,
            file: file
          });
          
          if (validPreviews.length === validFiles.length) {
            updateMedia(validFiles, validPreviews);
          }
        };
        reader.readAsDataURL(file);
      } else {
        // For videos, create object URL
        const videoUrl = URL.createObjectURL(file);
        validPreviews.push({
          url: videoUrl,
          type: 'video',
          name: file.name,
          file: file
        });
        
        if (validPreviews.length === validFiles.length) {
          updateMedia(validFiles, validPreviews);
        }
      }
    });

    if (newErrors.length > 0) {
      setErrors(newErrors);
    } else {
      setErrors([]);
    }
  };

  const updateMedia = (newFiles, newPreviews) => {
    const updatedFiles = [...mediaFiles, ...newFiles];
    const updatedPreviews = [...mediaPreviews, ...newPreviews];
    
    setMediaFiles(updatedFiles);
    setMediaPreviews(updatedPreviews);
    
    // Pass files to parent - ensure consistent File objects
    onMediaSelected(updatedFiles, updatedPreviews);
    
    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeMedia = (index) => {
    const newPreviews = mediaPreviews.filter((_, i) => i !== index);
    
    // Extract only the new files (those that have a .file property or are File objects)
    const newFiles = [];
    newPreviews.forEach(preview => {
      if (preview.file) {
        newFiles.push(preview.file);
      } else if (preview instanceof File) {
        newFiles.push(preview);
      }
    });
    
    // Clean up object URLs for videos
    if (mediaPreviews[index]?.type === 'video' && mediaPreviews[index]?.url?.startsWith('blob:')) {
      URL.revokeObjectURL(mediaPreviews[index].url);
    }
    
    setMediaFiles(newFiles);
    setMediaPreviews(newPreviews);
    
    // Pass both new files and current previews to parent so it can track what's removed
    onMediaSelected(newFiles, newPreviews);
  };

  const moveMedia = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= mediaPreviews.length) return;
    
    // Swap items in previews array
    const newPreviews = [...mediaPreviews];
    [newPreviews[fromIndex], newPreviews[toIndex]] = [newPreviews[toIndex], newPreviews[fromIndex]];
    
    // Extract only the new files (those that have a .file property or are File objects) in the new order
    const newFiles = [];
    newPreviews.forEach(preview => {
      if (preview.file) {
        newFiles.push(preview.file);
      } else if (preview instanceof File) {
        newFiles.push(preview);
      }
    });
    
    setMediaFiles(newFiles);
    setMediaPreviews(newPreviews);
    onMediaSelected(newFiles, newPreviews);
  };

  return (
    <div className="multi-media-upload">
      <div className="upload-area">
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,video/mp4,video/mpeg,video/quicktime,video/x-msvideo"
          onChange={handleFileChange}
          ref={fileInputRef}
          className="file-input"
          multiple
        />
        
        <div className="upload-info">
          <p className="upload-instructions">
            Upload images (JPG, PNG, GIF - max 2MB each) or videos (MP4, MPEG, MOV, AVI - max 10MB each)
          </p>
          <p className="file-count">
            {mediaFiles.length} / {maxFiles} files uploaded
          </p>
        </div>
        
        {errors.length > 0 && (
          <div className="upload-errors">
            {errors.map((error, index) => (
              <p key={index} className="error-message">{error}</p>
            ))}
          </div>
        )}
      </div>
      
      {mediaPreviews.length > 0 && (
        <div className="media-preview-grid">
          {mediaPreviews.map((media, index) => (
            <div key={index} className="media-preview-item">
              <div className="preview-container">
                {media.type === 'image' ? (
                  <img src={media.url} alt={`Preview ${index + 1}`} />
                ) : (
                  <video src={media.url} controls />
                )}
                
                <div className="preview-overlay">
                  <div className="preview-number">{index + 1}</div>
                  
                  <div className="preview-actions">
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => moveMedia(index, index - 1)}
                        className="move-button"
                        title="Move left"
                      >
                        ←
                      </button>
                    )}
                    
                    {index < mediaPreviews.length - 1 && (
                      <button
                        type="button"
                        onClick={() => moveMedia(index, index + 1)}
                        className="move-button"
                        title="Move right"
                      >
                        →
                      </button>
                    )}
                    
                    <button
                      type="button"
                      onClick={() => removeMedia(index)}
                      className="remove-button"
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
              
              <p className="media-name">{media.name || `Media ${index + 1}`}</p>
              {index === 0 && <span className="primary-badge">Primary</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MultiMediaUpload;